import { useId, useState } from "react"

import { Modal } from "../shared/Modal"
import { celebrationTypeOptions } from "./celebrationOptions"
import type {
  StayCelebrationTraveller,
  StayCelebrationType,
} from "./stayData"

export type CelebrationFormAttributes = {
  note: string
  occasion_date: string
  occasion_type: StayCelebrationType
  traveller_ids: readonly string[]
}

type CelebrationsFormProps = {
  error: string | null
  isBusy: boolean
  onAddOccasion: (attributes: CelebrationFormAttributes) => Promise<boolean>
  onClose: () => void
  selectedTravellerId?: string
  travellers: readonly StayCelebrationTraveller[]
}

export function CelebrationsForm({
  error,
  isBusy,
  onAddOccasion,
  onClose,
  selectedTravellerId,
  travellers,
}: CelebrationsFormProps) {
  const formId = useId()
  const typeSelectId = useId()
  const dateInputId = useId()
  const noteInputId = useId()
  const [selectedType, setSelectedType] = useState<StayCelebrationType | "">("")
  const [selectedTravellerIds, setSelectedTravellerIds] = useState<readonly string[]>(
    selectedTravellerId ? [selectedTravellerId] : [],
  )
  const [occasionDate, setOccasionDate] = useState("")
  const [note, setNote] = useState("")
  const selectedTraveller = selectedTravellerId
    ? travellers.find((traveller) => traveller.id === selectedTravellerId)
    : null
  const canAddOccasion = Boolean(
    !isBusy &&
      selectedType &&
      (selectedTravellerId || selectedTravellerIds.length),
  )

  function toggleTraveller(travellerId: string) {
    setSelectedTravellerIds((currentIds) =>
      currentIds.includes(travellerId)
        ? currentIds.filter((id) => id !== travellerId)
        : [...currentIds, travellerId],
    )
  }

  async function addOccasion() {
    if (!canAddOccasion || !selectedType) return

    const saved = await onAddOccasion({
      note: note.trim(),
      occasion_date: occasionDate,
      occasion_type: selectedType,
      traveller_ids: selectedTravellerId ? [selectedTravellerId] : selectedTravellerIds,
    })

    if (saved) {
      onClose()
    }
  }

  return (
    <Modal
      footer={(
        <>
          <button
            className="h-8 rounded-[4px] border border-[#344149] bg-white px-3 text-xs font-medium leading-4 text-[#344149] hover:bg-[#f5f5f5] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isBusy}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="h-8 rounded-[4px] bg-[#344149] px-3 text-xs font-medium leading-4 text-white hover:bg-[#263138] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={!canAddOccasion}
            form={formId}
            type="submit"
          >
            Add
          </button>
        </>
      )}
      onClose={onClose}
      open
      title="ADD CELEBRATION"
    >
      <form
        className="flex flex-col gap-6"
        id={formId}
        onSubmit={(event) => {
          event.preventDefault()
          void addOccasion()
        }}
      >
        {selectedTraveller ? (
          <section className="flex flex-col gap-2">
            <h3 className="text-base font-medium leading-[19px] text-[#1c1c1f]">Who is this for?</h3>
            <p className="text-xs font-normal leading-[15px] text-[#6b7078]">
              {selectedTraveller.display_name}
            </p>
          </section>
        ) : (
          <section className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <h3 className="text-base font-medium leading-[19px] text-[#1c1c1f]">Who is this for?</h3>
              <p className="text-xs font-normal leading-[15px] text-[#6b7078]">
                Choose one or more guests if it applies to more than one guest.
              </p>
            </div>

            <div aria-label="Celebration guests" className="flex flex-wrap gap-2.5" role="group">
              {travellers.map((traveller) => {
                const isSelected = selectedTravellerIds.includes(traveller.id)

                return (
                  <button
                    aria-pressed={isSelected}
                    className={[
                      "inline-flex h-8 items-center gap-2 overflow-hidden whitespace-nowrap rounded-[24px] border px-3 py-2 text-[13px] leading-normal focus:outline-none focus-visible:ring-2 focus-visible:ring-[#337ab7] focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                      isSelected
                        ? "border-[#344149] bg-[#d2dadf] font-medium text-[#344149]"
                        : "border-[#d4d4d4] bg-[#f5f5f5] font-normal text-black hover:border-[#344149] hover:bg-[#e7ebee] hover:font-medium hover:text-[#344149]",
                    ].join(" ")}
                    disabled={isBusy}
                    key={traveller.id}
                    onClick={() => toggleTraveller(traveller.id)}
                    type="button"
                  >
                    {travellerChipLabel(traveller)}
                  </button>
                )
              })}
            </div>
          </section>
        )}

        <section className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h3 className="text-base font-medium leading-[19px] text-[#1c1c1f]">Add Celebration Details</h3>
            <p className="text-xs font-normal leading-[15px] text-[#6b7078]">
              Specify the celebration type, add a date if known, and include a note if it would help other teams better understand the details of this celebration.
            </p>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row">
            <label className="sr-only" htmlFor={typeSelectId}>
              Celebration type
            </label>
            <div className="relative min-w-0 flex-1">
              <select
                className="h-10 w-full appearance-none rounded-[6px] border border-[#a3a3a3] bg-white px-2.5 pr-9 text-xs font-normal text-black outline-none invalid:text-[#a3a3a3] focus:border-[#337ab7] focus:ring-2 focus:ring-[#337ab7]/20 disabled:cursor-not-allowed disabled:opacity-50"
                disabled={isBusy}
                id={typeSelectId}
                onChange={(event) => setSelectedType(event.target.value as StayCelebrationType | "")}
                required
                value={selectedType}
              >
                <option value="">Select celebration type</option>
                {celebrationTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <span
                aria-hidden="true"
                className="pointer-events-none absolute right-3 top-1/2 size-2 -translate-y-[65%] rotate-45 border-b border-r border-[#a3a3a3]"
              />
            </div>

            <label className="sr-only" htmlFor={dateInputId}>
              Celebration date
            </label>
            <input
              className="h-10 min-w-0 rounded-[6px] border border-[#a3a3a3] bg-white px-2.5 text-xs font-normal text-black outline-none placeholder:text-[#a3a3a3] focus:border-[#337ab7] focus:ring-2 focus:ring-[#337ab7]/20 disabled:cursor-not-allowed disabled:opacity-50 sm:w-[172px]"
              disabled={isBusy}
              id={dateInputId}
              onChange={(event) => setOccasionDate(event.target.value)}
              placeholder="Select date"
              type="date"
              value={occasionDate}
            />
          </div>

          <label className="sr-only" htmlFor={noteInputId}>
            Celebration note
          </label>
          <input
            className="h-10 min-w-0 rounded-[6px] border border-[#a3a3a3] bg-white px-2.5 text-xs font-normal text-black outline-none placeholder:text-[#a3a3a3] focus:border-[#337ab7] focus:ring-2 focus:ring-[#337ab7]/20 disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isBusy}
            id={noteInputId}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Add note..."
            type="text"
            value={note}
          />
        </section>

        {error ? (
          <p className="rounded-[4px] border border-[#e0b4b4] bg-[#fff2f2] px-3 py-2 text-sm font-bold text-[#873230]">
            {error}
          </p>
        ) : null}
      </form>
    </Modal>
  )
}

function travellerChipLabel(traveller: StayCelebrationTraveller) {
  return traveller.display_name.split(" ")[0] || traveller.display_name
}
